/**
 * Types shared between frontend and backend for PDF processing
 */
export interface TextItem {
  text: string;           // The actual text content
  x: number;             // X coordinate on page
  y: number;             // Y coordinate on page
  font?: string;         // Font name/family
  bold?: boolean;        // Whether text is bold
  pageNumber?: number;   // Page number this text appears on
  width?: number;        // Width of text block
  height?: number;       // Height of text block
  fontSize?: number;     // Font size if available
}

/**
 * Parser errors with specific error codes
 */
export enum ParserErrorCode {
  EXTRACTION_FAILED = 'EXTRACTION_FAILED',
  VALIDATION_FAILED = 'VALIDATION_FAILED',
  PARSING_FAILED = 'PARSING_FAILED',
  NETWORK_ERROR = 'NETWORK_ERROR',
  UNKNOWN_ERROR = 'UNKNOWN_ERROR'
}

export class PDFExtractionError extends Error {
  constructor(
    message: string,
    public code: ParserErrorCode = ParserErrorCode.EXTRACTION_FAILED,
    public details?: unknown
  ) {
    super(message);
    this.name = 'PDFExtractionError';
  }
}

export class ParserError extends Error {
  constructor(
    message: string,
    public code: ParserErrorCode = ParserErrorCode.UNKNOWN_ERROR,
    public details?: unknown
  ) {
    super(message);
    this.name = 'ParserError';
  }
}

/**
 * Response from the parser API
 */
export interface ParsedResume {
  name: string;
  email?: string;
  phone_number?: string;
  educations?: Education[];
  education?: { institution: string; degree: string; date: string }[];
  work_experiences?: WorkExperience[];
  experience?: { company: string; position: string; duration: string }[];
  languages: string[];
  skills: string[] | Skills;
  total_experience?: number;
  // Source tracking fields
  parsed_locally?: boolean;
  parsed_hybrid?: boolean;
  parsed_by_fallback?: boolean;
  api_error?: string;
  raw_text?: string;
  _debug?: {
    profile_scores: any;
    education_scores: any[];
    work_experience_scores: any[];
    language_scores: any[];
  };
}

export interface Education {
  school: string;
  degree: string;
  gpa: string | null;
  date: string | null;
  descriptions: string[];
}

export interface WorkExperience {
  company: string;
  job_title: string;
  date: string | null;
  descriptions: string[];
}

export interface Skills {
  featured: string[];
  descriptions: string[];
}

/**
 * Runtime type guards
 */
export function isTextItem(obj: any): obj is TextItem {
  return (
    typeof obj === 'object' &&
    obj !== null &&
    typeof obj.text === 'string' &&
    typeof obj.x === 'number' &&
    typeof obj.y === 'number'
  );
}

export function validateTextItem(item: any): TextItem {
  if (!isTextItem(item)) {
    throw new PDFExtractionError(`Invalid TextItem: ${JSON.stringify(item)}`, ParserErrorCode.VALIDATION_FAILED);
  }
  return {
    text: item.text,
    x: item.x,
    y: item.y,
    font: item.font || 'unknown',
    bold: item.bold || false,
    pageNumber: item.pageNumber || 1,
    width: item.width,
    height: item.height,
    fontSize: item.fontSize
  };
}

export function validateTextItems(items: any[]): TextItem[] {
  if (!Array.isArray(items)) {
    throw new PDFExtractionError('Expected array of TextItems', ParserErrorCode.VALIDATION_FAILED);
  }
  return items.map(validateTextItem);
}
